import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, sortableKeyboardCoordinates, rectSortingStrategy } from '@dnd-kit/sortable';
import { ImagesIcon, WarningOctagonIcon } from '@phosphor-icons/react';
import { Card, CardHeading } from '@/components/ui/card';
import { FieldLabel } from '@/components/ui/text';
import { SortableImageItem } from '@/components/cms/ui/sortable-image-item';
import LureoButton from '@/common/LureoButton';
import { validateField, getImageErrorClass } from '@/utils/fieldHighlighting';
import type { ProjectImages } from '@/modules/projectImages';

type Category = 'ProjectPortrait' | 'ProjectLandscape';

interface Props {
  projectImages: ProjectImages;
  readFailed: boolean;
  onRetry: () => void;
  handleImageUpload: (category: Category, imageId?: string) => void;
  handleImageDelete: (imageId: string, category: Category) => void;
  handleLandscapeImageReorder: (event: { active: { id: string }; over: { id: string } | null }) => void;
  showValidation: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ProjectHeaderImagesCard({
  projectImages, readFailed, onRetry, handleImageUpload, handleImageDelete, handleLandscapeImageReorder, showValidation, open, onOpenChange,
}: Props) {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  return (
    <Card elevation="high">
      <CardHeading icon={<ImagesIcon />} title="Header Images" open={open} onOpenChange={onOpenChange} />

      {open && (readFailed ? (
        <div className="flex flex-col items-center gap-3 py-6 text-center">
          <WarningOctagonIcon className="h-6 w-6 text-lureo-warning-ink" />
          <p className="text-lureo-14 text-lureo-ink-subtle leading-5">We couldn't load the project images.</p>
          <LureoButton variant="outline-2" className="h-9 rounded-lureo-radius-button-pill px-4" onClick={onRetry}>Try again</LureoButton>
        </div>
      ) : (
        <>
          <div className="flex flex-col lg:flex-row items-start justify-center gap-6">
            {/* Portrait Image - Left Side */}
            <div className="shrink-0 w-full lg:w-auto flex flex-col items-center lg:items-start">
              <FieldLabel variant="plain" mb>Portrait Image</FieldLabel>
              {projectImages.portrait ? (
                <SortableImageItem
                  image={{
                    id: projectImages.portrait.id,
                    url: projectImages.portrait.url,
                  }}
                  index={0}
                  variant="portrait"
                  onImageUpload={() => handleImageUpload('ProjectPortrait', projectImages.portrait?.id)}
                  onRemoveImage={() => handleImageDelete(projectImages.portrait.id, 'ProjectPortrait')}
                />
              ) : (
                <div className={`w-40 h-64 rounded-lureo-radius-lg bg-lureo-page transition-colors border-lureo-upload-line-width border-dashed hover:border-lureo-brand-ink/50 ${
                  getImageErrorClass(!validateField('project_portrait_image', projectImages.portrait), showValidation)
                } ${!validateField('project_portrait_image', projectImages.portrait) && showValidation ? '' : 'border-lureo-ink-subtle'}`}>
                  <div className="h-full flex flex-col items-center justify-center p-2">
                    <ImagesIcon className="mx-auto h-6 w-6 text-lureo-ink-subtle mb-2" />
                    <p className="text-lureo-12 text-lureo-ink-subtle mb-2 leading-4">Required</p>
                    <LureoButton
                      variant="outline-2"
                      onClick={() => handleImageUpload('ProjectPortrait')}
                      className="h-7 rounded-lureo-radius-button-pill bg-lureo-surface px-3 text-lureo-12 font-medium"
                    >
                      Upload
                    </LureoButton>
                  </div>
                </div>
              )}
            </div>

            {/* Landscape Images - Right Side */}
            <div className="flex-1 w-full lg:w-auto flex flex-col items-center lg:items-start">
              <FieldLabel variant="plain" mb>Landscape Images</FieldLabel>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={(event) => {
                  const { active, over } = event;
                  handleLandscapeImageReorder({
                    active: { id: active.id as string },
                    over: over ? { id: over.id as string } : null
                  });
                }}
              >
                <SortableContext
                  items={projectImages.landscape.map(img => img.id)}
                  strategy={rectSortingStrategy}
                >
                  <div className="flex flex-row flex-wrap gap-4 justify-center lg:justify-start">
                    {projectImages.landscape.map((image, index) => (
                      <SortableImageItem
                        key={image.id}
                        image={{
                          id: image.id,
                          url: image.url,
                        }}
                        index={index}
                        variant="landscape"
                        onImageUpload={() => handleImageUpload('ProjectLandscape', image.id)}
                        onRemoveImage={() => handleImageDelete(image.id, 'ProjectLandscape')}
                      />
                    ))}
                    {/* Add placeholder for new landscape image - only show if less than 3 images */}
                    {projectImages.landscape.length < 3 && (
                      <div className={`w-full max-w-72 h-40 sm:w-64 sm:h-36 rounded-lureo-radius-lg bg-lureo-page transition-colors border-lureo-upload-line-width border-dashed hover:border-lureo-brand-ink/50 ${
                        getImageErrorClass(!validateField('project_landscape_image', projectImages.landscape), showValidation)
                      } ${!validateField('project_landscape_image', projectImages.landscape) && showValidation ? '' : 'border-lureo-ink-subtle'}`}>
                        <div className="h-full flex flex-col items-center justify-center p-2">
                          <ImagesIcon className="mx-auto h-5 w-5 text-lureo-ink-subtle mb-1" />
                          <p className="text-lureo-12 text-lureo-ink-subtle mb-1 leading-4">Add Image</p>
                          <LureoButton
                            variant="outline-2"
                            onClick={() => handleImageUpload('ProjectLandscape')}
                            className="h-7 rounded-lureo-radius-button-pill bg-lureo-surface px-3 text-lureo-12 font-medium"
                          >
                            Upload
                          </LureoButton>
                        </div>
                      </div>
                    )}
                  </div>
                </SortableContext>
              </DndContext>
            </div>
          </div>

          <p className="mt-3 text-lureo-12 text-lureo-ink-subtle leading-4">
            Upload one portrait (9:16) and multiple landscape (16:9) images. Drag and drop to reorder landscape images.
          </p>
        </>
      ))}
    </Card>
  );
}
